import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import RecipeDetailModal from './RecipeDetailModal';
import FeedbackButtons from './FeedbackButtons';
import Icon from './Icon';
import Toast from './Toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/recipes';

interface Recipe {
  id: number;
  name: string;
  cuisine: string;
  prep_time: number;
  cook_time: number;
  difficulty: string;
  ingredients: any[];
  instructions: string;
  saved: boolean;
  feedback: string; // '' | 'liked' | 'disliked'
}

interface Meal {
  id: number;
  date: string;
  meal_type: string;
  scheduled_time: string;
  status: string;
  recipe: Recipe;
}

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner'];

const Dashboard: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  // The meal currently open via the Cook button (as opposed to View
  // Recipe) -- lets the modal know which meal to mark cooked when done.
  const [cookingMeal, setCookingMeal] = useState<Meal | null>(null);
  const [movingMeal, setMovingMeal] = useState<Meal | null>(null);
  const [moveDate, setMoveDate] = useState('');
  const [moveMealType, setMoveMealType] = useState('dinner');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const errorDetail = (error: unknown): string | null =>
    axios.isAxiosError(error) ? error.response?.data?.error ?? null : null;

  const { data: meals, isLoading } = useQuery({
    queryKey: ['meal-schedule'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/schedule/`);
      return response.data as Meal[];
    }
  });

  const cookMutation = useMutation({
    mutationFn: async (mealId: number) => {
      const response = await axios.post(`${API_URL}/schedule/${mealId}/cook/`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meal-schedule'] });
      setCookingMeal(null);
    }
  });

  const toggleLikeMutation = useMutation({
    mutationFn: async (recipe: Recipe) => {
      if (recipe.saved) {
        await axios.post(`${API_URL}/my-recipes/${recipe.id}/unsave/`);
      } else {
        await axios.post(`${API_URL}/my-recipes/like/`, { id: recipe.id });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meal-schedule'] });
      queryClient.invalidateQueries({ queryKey: ['my-recipes'] });
    },
  });

  const feedbackMutation = useMutation({
    mutationFn: async ({ recipe, feedback }: { recipe: Recipe; feedback: string }) => {
      await axios.post(`${API_URL}/my-recipes/${recipe.id}/feedback/`, { feedback });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meal-schedule'] });
    },
    onError: (error) => {
      setMessage({ type: 'error', text: errorDetail(error) || 'Error recording feedback.' });
    },
  });

  const handleThumbsUp = (recipe: Recipe) => {
    feedbackMutation.mutate({ recipe, feedback: recipe.feedback === 'liked' ? '' : 'liked' });
  };

  // Thumbs-down on the Meal Plan is more than a preference flag -- it
  // replaces this slot with a freshly generated alternate and regenerates
  // the shopping list, so it's confirmed like the other plan-altering
  // actions (Delete/Move) rather than toggled like the plain like/dislike
  // used everywhere else.
  const thumbsDownMutation = useMutation({
    mutationFn: async (mealId: number) => {
      const response = await axios.post(`${API_URL}/schedule/${mealId}/thumbs-down/`);
      return response.data as { replaced: boolean; message: string };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['meal-schedule'] });
      queryClient.invalidateQueries({ queryKey: ['shopping-list'] });
      setMessage({ type: data.replaced ? 'success' : 'error', text: data.message });
    },
    onError: (error) => {
      setMessage({ type: 'error', text: errorDetail(error) || 'Error recording feedback.' });
    },
  });

  const handleThumbsDown = (meal: Meal) => {
    if (window.confirm(`Not a fan of ${meal.recipe.name}? We'll swap in something else for this slot.`)) {
      thumbsDownMutation.mutate(meal.id);
    }
  };

  const deleteMealMutation = useMutation({
    mutationFn: async (mealId: number) => {
      await axios.delete(`${API_URL}/schedule/${mealId}/delete/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meal-schedule'] });
      setMessage({ type: 'success', text: 'Meal removed from your plan.' });
    },
    onError: (error) => {
      setMessage({ type: 'error', text: errorDetail(error) || 'Error removing meal.' });
    },
  });

  const handleDeleteMeal = (meal: Meal) => {
    if (window.confirm(`Remove ${meal.recipe.name} from your meal plan?`)) {
      deleteMealMutation.mutate(meal.id);
    }
  };

  const moveMutation = useMutation({
    mutationFn: async ({ id, date, meal_type }: { id: number; date: string; meal_type: string }) => {
      const response = await axios.post(`${API_URL}/schedule/${id}/move/`, { date, meal_type });
      return response.data as { swapped_with: number | null };
    },
    onSuccess: ({ swapped_with }) => {
      queryClient.invalidateQueries({ queryKey: ['meal-schedule'] });
      setMovingMeal(null);
      setMessage({
        type: 'success',
        text: swapped_with ? 'Meals swapped.' : 'Meal moved.',
      });
    },
    onError: (error) => {
      setMessage({ type: 'error', text: errorDetail(error) || 'Error moving meal.' });
    },
  });

  const openMoveModal = (meal: Meal) => {
    setMovingMeal(meal);
    setMoveDate(meal.date);
    setMoveMealType(meal.meal_type);
  };

  const getMealIcon = (mealType: string) => {
    const icons: Record<string, string> = {
      breakfast: 'sunrise',
      lunch: 'sun',
      dinner: 'moon'
    };
    return icons[mealType] || 'utensils';
  };

  const getStatusBadge = (status: string) => {
    // Must match MealSchedule.STATUS_CHOICES in models.py
    const badges: Record<string, { text: string; class: string }> = {
      scheduled: { text: 'Scheduled', class: 'status-scheduled' },
      completed: { text: 'Cooked', class: 'status-completed' },
      cancelled: { text: 'Cancelled', class: 'status-cancelled' }
    };
    return badges[status] || badges.scheduled;
  };

  const groupMealsByDate = (meals: Meal[]) => {
    const grouped: Record<string, Meal[]> = {};
    meals.forEach(meal => {
      if (!grouped[meal.date]) {
        grouped[meal.date] = [];
      }
      grouped[meal.date].push(meal);
    });
    return grouped;
  };

  const formatDate = (dateString: string) => {
    // A bare "YYYY-MM-DD" string parses as UTC midnight per spec, which
    // shifts to the previous day once displayed in any timezone behind
    // UTC (most of the US) -- forcing local-time parsing instead.
    const date = new Date(`${dateString}T00:00:00`);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    } else {
      return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }
  };

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>Loading meal plan...</p>
      </div>
    );
  }

  if (!meals || meals.length === 0) {
    return (
      <div className="dashboard">
        <div className="empty-state">
          <Icon name="calendar" className="icon empty-icon" />
          <h2>No Meal Plan Yet</h2>
          <p>Generate a meal plan in Settings to get started!</p>
        </div>
      </div>
    );
  }

  const groupedMeals = groupMealsByDate(meals);
  const sortedDates = Object.keys(groupedMeals).sort();

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1><Icon name="calendar" className="icon" />Weekly Meal Plan</h1>
        <p>{meals.length} meals planned (today through the next 10 days)</p>
      </div>

      <div className="meal-timeline">
        {sortedDates.map(date => (
          <div key={date} className="day-group">
            <div className="day-header">
              <h3>{formatDate(date)}</h3>
            </div>

            <div className="meals-grid">
              {groupedMeals[date]
                .sort((a, b) => a.scheduled_time.localeCompare(b.scheduled_time))
                .map(meal => {
                  const statusBadge = getStatusBadge(meal.status);
                  return (
                    <div key={meal.id} className="meal-card">
                      <div className="meal-header">
                        <div className="meal-header-left">
                          <div className="meal-type">
                            <Icon name={getMealIcon(meal.meal_type)} className="icon meal-icon" />
                            <span className="meal-label">{meal.meal_type}</span>
                          </div>
                          <button
                            className={`like-btn ${meal.recipe.saved ? 'liked' : ''}`}
                            onClick={() => toggleLikeMutation.mutate(meal.recipe)}
                            disabled={toggleLikeMutation.isPending}
                            aria-label={meal.recipe.saved ? 'Remove from My Recipes' : 'Save to My Recipes'}
                          >
                            <Icon name="heart" className="icon" />
                          </button>
                          <FeedbackButtons
                            feedback={meal.recipe.feedback}
                            onThumbsUp={() => handleThumbsUp(meal.recipe)}
                            onThumbsDown={() => handleThumbsDown(meal)}
                            disabled={feedbackMutation.isPending || thumbsDownMutation.isPending}
                          />
                        </div>
                        <div className="meal-header-right">
                          <span className={`meal-status ${statusBadge.class}`}>
                            {statusBadge.text}
                          </span>
                          <button
                            className="icon-btn"
                            onClick={() => openMoveModal(meal)}
                            aria-label={`Move ${meal.recipe.name}`}
                          >
                            <Icon name="move" className="icon icon-sm" />
                          </button>
                          <button
                            className="icon-btn"
                            onClick={() => handleDeleteMeal(meal)}
                            aria-label={`Remove ${meal.recipe.name}`}
                          >
                            <Icon name="trash" className="icon icon-sm" />
                          </button>
                        </div>
                      </div>

                      <h3 className="meal-name">{meal.recipe.name}</h3>
                      <div className="meal-meta">
                        <span>{meal.recipe.cuisine}</span>
                        <span><Icon name="clock" className="icon icon-sm" /> {meal.recipe.prep_time + meal.recipe.cook_time} min</span>
                      </div>

                      <div className="meal-actions">
                        <button
                          onClick={() => setSelectedRecipe(meal.recipe)}
                          className="btn-secondary"
                        >
                          View Recipe
                        </button>
                        <button
                          onClick={() => setCookingMeal(meal)}
                          disabled={meal.status === 'completed'}
                          className="btn-primary"
                        >
                          <Icon name={meal.status === 'completed' ? 'check' : 'flame'} className="icon icon-sm" />
                          {meal.status === 'completed' ? 'Cooked' : 'Cook'}
                        </button>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        ))}
      </div>

      {selectedRecipe && (
        <RecipeDetailModal recipe={selectedRecipe} onClose={() => setSelectedRecipe(null)} />
      )}

      {cookingMeal && (
        <RecipeDetailModal
          recipe={cookingMeal.recipe}
          onClose={() => setCookingMeal(null)}
          onMarkCooked={() => cookMutation.mutate(cookingMeal.id)}
          marking={cookMutation.isPending}
        />
      )}

      {message && (
        <Toast type={message.type} text={message.text} onDismiss={() => setMessage(null)} />
      )}

      {movingMeal && (
        <div className="modal-overlay" onClick={() => setMovingMeal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setMovingMeal(null)}>✕</button>
            <h2><Icon name="move" className="icon" />Move Meal</h2>
            <p className="scan-review-hint">{movingMeal.recipe.name}</p>

            <div className="form-row">
              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  value={moveDate}
                  onChange={(e) => setMoveDate(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Meal</label>
                <select value={moveMealType} onChange={(e) => setMoveMealType(e.target.value)}>
                  {MEAL_TYPES.map((m) => (
                    <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-actions">
              <button type="button" onClick={() => setMovingMeal(null)} className="btn-secondary">
                Cancel
              </button>
              <button
                type="button"
                onClick={() => moveMutation.mutate({ id: movingMeal.id, date: moveDate, meal_type: moveMealType })}
                disabled={moveMutation.isPending || !moveDate}
                className="btn-primary"
              >
                {moveMutation.isPending ? 'Moving...' : 'Move Meal'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;